import { Component, effect, inject, signal } from '@angular/core'
import { Router } from '@angular/router'
import { AuthService } from '../../../infrastructure/auth/auth.service'
import { JamarLogoComponent } from '../../components/jamar-logo'
import { CLAVE_ULTIMO_MODULO, MODULOS, type OpcionModulo } from '../../../domain/modulo'

/**
 * Puerta de entrada de la plataforma Jamar — Feria y Calidad son módulos
 * hermanos, aquí se elige a cuál entrar ANTES de loguearse. Rediseño
 * "Harbor Night" (2026-10-12, ver Jamar_Gateway_Rediseno_Ultradetallado.md):
 * las dos tarjetas son accesos directos, no hay tabs — eso evita un
 * selector redundante sobre dos tarjetas que ya funcionan como entradas
 * independientes.
 *
 * Regla de UX ya existente: si ya hay sesión activa, no se vuelve a
 * preguntar — se salta directo al último módulo elegido (guardado en
 * sessionStorage, nunca localStorage, porque es una preferencia de sesión
 * del navegador, no un dato sensible a persistir para siempre).
 */
@Component({
  selector: 'app-module-gateway',
  standalone: true,
  imports: [JamarLogoComponent],
  templateUrl: './module-gateway.html',
  styleUrl: './module-gateway.scss',
})
export class ModuleGatewayComponent {
  private readonly auth = inject(AuthService)
  private readonly router = inject(Router)

  readonly modulos = MODULOS
  /** true solo cuando ya sabemos que hay que mostrar el selector (sin sesión) — evita el parpadeo si hay que redirigir directo. */
  readonly listo = signal(false)

  constructor() {
    effect(() => {
      if (this.auth.cargando()) return
      const ultimo = this.auth.perfil() ? this.leerUltimoModulo() : null
      if (ultimo) {
        void this.router.navigateByUrl(ultimo.rutaDestino, { replaceUrl: true })
        return
      }
      this.listo.set(true)
    })
  }

  entrar(modulo: OpcionModulo): void {
    try {
      sessionStorage.setItem(CLAVE_ULTIMO_MODULO, modulo.id)
    } catch {
      // Privacidad/incognito puede bloquear sessionStorage — no es crítico, solo se pierde el atajo de "recordar módulo".
    }
    void this.router.navigateByUrl(modulo.rutaLogin)
  }

  /**
   * `null` cuando NO hay un módulo realmente recordado en esta pestaña —
   * antes caía al primero de la lista (Feria) como "default", lo que hacía
   * que un usuario de Calidad con sesión activa pero sessionStorage vacío
   * (típico al cerrar y reabrir la app: sessionStorage muere con la pestaña,
   * la sesión de login en localStorage no) terminara saltando siempre a
   * Feria sin que se le preguntara nada (2026-10-20). Sin un recuerdo real,
   * se debe mostrar el selector, no asumir un módulo.
   */
  private leerUltimoModulo(): OpcionModulo | null {
    try {
      const id = sessionStorage.getItem(CLAVE_ULTIMO_MODULO)
      return this.modulos.find((m) => m.id === id) ?? null
    } catch {
      return null
    }
  }
}
